import { types } from "@vilocnv/allsetra-core";
import { DateTime } from "luxon";
import { round } from "lodash";
import { IObjectRideSegment } from "../types";
import {
  IObjectRideWithSegments,
  ObjectRideModeEnum
} from "./objectRideHelpers";

export const MIXED_RIDE_SEGMENT_COLORS = {
  business: "#00B2A3",
  private: "#323946"
} as const;

export type NormalizedSegmentMode =
  | ObjectRideModeEnum.BUSINESS
  | ObjectRideModeEnum.PRIVATE;

export type MixedRideModeTotals = {
  businessKm: number;
  privateKm: number;
  businessPercent: number;
  privatePercent: number;
  totalKm: number;
};

export type MixedRideTimelineStop = {
  date: string;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
  followingSegment?: IObjectRideSegment | null;
};

export type MixedRideSegmentEndpoint = {
  date: string;
  address: string;
  latitude: number;
  longitude: number;
};

export type MixedRideTransitionPoint = {
  uniqueId: string;
  latitude: number;
  longitude: number;
  date: string;
  fromMode: IObjectRideSegment["mode"];
  toMode: IObjectRideSegment["mode"];
  heading: number;
};

export type MixedRidePathArrow = {
  uniqueId: string;
  latitude: number;
  longitude: number;
  heading: number;
};

const PRIVACY_GAP_MS = 1000;
const POLYLINE_STITCH_MS = 2 * 60 * 1000;

const parseLocationTime = (date?: string | null) => {
  if (!date) return Number.NaN;
  const millis = Date.parse(date);
  return Number.isNaN(millis) ? Number.NaN : millis;
};

export const isUtcMidnight = (date?: string | null) => {
  if (!date) return false;
  const parsed = DateTime.fromISO(date, { zone: "utc" });
  return (
    parsed.isValid &&
    parsed.hour === 0 &&
    parsed.minute === 0 &&
    parsed.second === 0
  );
};

const hasTimeGap = (fromDate?: string | null, toDate?: string | null) => {
  const from = parseLocationTime(fromDate);
  const to = parseLocationTime(toDate);
  return !Number.isNaN(from) && !Number.isNaN(to) && to - from > PRIVACY_GAP_MS;
};

const makeHiddenPrivateSegment = (
  uniqueId: string,
  startDate: string,
  endDate: string
): IObjectRideSegment => ({
  uniqueId,
  mode: ObjectRideModeEnum.PRIVATE,
  startDate,
  endDate,
  startMileage: null,
  endMileage: null,
  totalMileage: null,
  isPrivacyHidden: true
});

export const fillMixedRidePrivacyGaps = (
  ride: IObjectRideWithSegments | types.IObjectRide | null | undefined,
  segments: IObjectRideSegment[] = []
): IObjectRideSegment[] => {
  if (!ride || !segments.length) return segments;

  const sorted = [...segments].sort(
    (left, right) =>
      parseLocationTime(left.startDate) - parseLocationTime(right.startDate)
  );
  const filled: IObjectRideSegment[] = [];
  const first = sorted[0];

  if (
    ride.startDate &&
    first.startDate &&
    hasTimeGap(ride.startDate, first.startDate) &&
    normalizeSegmentMode(first.mode) !== ObjectRideModeEnum.PRIVATE
  ) {
    filled.push(
      makeHiddenPrivateSegment(
        `privacy-gap-start-${first.uniqueId}`,
        ride.startDate,
        first.startDate
      )
    );
  }

  sorted.forEach((segment, index) => {
    filled.push(segment);
    const next = sorted[index + 1];
    if (!next || !segment.endDate || !next.startDate) return;
    if (
      normalizeSegmentMode(segment.mode) === ObjectRideModeEnum.PRIVATE ||
      normalizeSegmentMode(next.mode) === ObjectRideModeEnum.PRIVATE
    ) {
      return;
    }
    if (hasTimeGap(segment.endDate, next.startDate)) {
      filled.push(
        makeHiddenPrivateSegment(
          `privacy-gap-${segment.uniqueId}-${next.uniqueId}`,
          segment.endDate,
          next.startDate
        )
      );
    }
  });

  const last = sorted[sorted.length - 1];
  if (
    ride.endDate &&
    last.endDate &&
    hasTimeGap(last.endDate, ride.endDate) &&
    normalizeSegmentMode(last.mode) !== ObjectRideModeEnum.PRIVATE
  ) {
    filled.push(
      makeHiddenPrivateSegment(
        `privacy-gap-end-${last.uniqueId}`,
        last.endDate,
        ride.endDate
      )
    );
  }

  return filled;
};

export const normalizeSegmentMode = (
  mode: IObjectRideSegment["mode"] | null | undefined
): NormalizedSegmentMode | null => {
  if (mode === ObjectRideModeEnum.BUSINESS) {
    return ObjectRideModeEnum.BUSINESS;
  }
  if (mode === ObjectRideModeEnum.PRIVATE) {
    return ObjectRideModeEnum.PRIVATE;
  }
  if (typeof mode === "string") {
    const normalized = mode.trim().toUpperCase();
    if (normalized === "BUSINESS") return ObjectRideModeEnum.BUSINESS;
    if (normalized === "PRIVATE") return ObjectRideModeEnum.PRIVATE;
  }
  return null;
};

export const isMixedRideWithSegments = (
  ride?: IObjectRideWithSegments | types.IObjectRide | null
): ride is IObjectRideWithSegments & { segments: IObjectRideSegment[] } => {
  if (!ride || ride.rideMode !== ObjectRideModeEnum.MIXED) {
    return false;
  }
  const segments = (ride as IObjectRideWithSegments).segments;
  return Array.isArray(segments) && segments.length > 0;
};

export const getMixedRideDisplaySegments = (
  ride?: IObjectRideWithSegments | types.IObjectRide | null
): IObjectRideSegment[] => {
  if (!isMixedRideWithSegments(ride)) return [];
  return fillMixedRidePrivacyGaps(ride, ride.segments);
};

export const getSegmentDistanceKm = (
  segment?: IObjectRideSegment | null
): number => {
  if (!segment) return 0;

  const { startMileage, endMileage, totalMileage } = segment;
  if (
    typeof startMileage === "number" &&
    Number.isFinite(startMileage) &&
    typeof endMileage === "number" &&
    Number.isFinite(endMileage)
  ) {
    return round(Math.max(0, endMileage - startMileage), 2);
  }

  if (typeof totalMileage === "number" && Number.isFinite(totalMileage)) {
    return round(Math.max(0, totalMileage), 2);
  }

  return 0;
};

export const getSegmentDurationMinutes = (
  segment?: IObjectRideSegment | null
): number => {
  if (!segment?.startDate) return 0;
  const start = DateTime.fromISO(segment.startDate);
  const end = segment.endDate
    ? DateTime.fromISO(segment.endDate)
    : DateTime.invalid("missing end");
  if (!start.isValid || !end.isValid) return 0;
  return Math.max(0, Math.round(end.diff(start, "minutes").minutes));
};

export const getMixedRideModeTotals = (
  segments: IObjectRideSegment[] = [],
  fallbackPrivateMileage?: number | null
): MixedRideModeTotals => {
  let businessKm = 0;
  let privateKm = 0;

  segments.forEach((segment) => {
    if (segment.isPrivacyHidden) return;
    const mode = normalizeSegmentMode(segment.mode);
    const distance = getSegmentDistanceKm(segment);
    if (mode === ObjectRideModeEnum.BUSINESS) {
      businessKm += distance;
    } else if (mode === ObjectRideModeEnum.PRIVATE) {
      privateKm += distance;
    }
  });

  businessKm = round(businessKm, 2);
  privateKm = round(privateKm, 2);

  if (
    privateKm <= 0 &&
    typeof fallbackPrivateMileage === "number" &&
    Number.isFinite(fallbackPrivateMileage) &&
    fallbackPrivateMileage > 0
  ) {
    privateKm = round(fallbackPrivateMileage, 2);
  }

  const totalKm = round(businessKm + privateKm, 2);

  if (totalKm <= 0) {
    return {
      businessKm,
      privateKm,
      businessPercent: 0,
      privatePercent: 0,
      totalKm
    };
  }

  const businessPercent = Math.round((businessKm / totalKm) * 100);
  return {
    businessKm,
    privateKm,
    businessPercent,
    privatePercent: 100 - businessPercent,
    totalKm
  };
};

export const getSegmentColor = (
  mode: IObjectRideSegment["mode"] | null | undefined
): string => {
  const normalized = normalizeSegmentMode(mode);
  return normalized === ObjectRideModeEnum.PRIVATE
    ? MIXED_RIDE_SEGMENT_COLORS.private
    : MIXED_RIDE_SEGMENT_COLORS.business;
};

export const getMapBearing = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): number => {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const metersBetween = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
) => {
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const haversine =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(haversine)));
};

const isSameCoordinate = (
  left: { lat: number; lng: number },
  right: { lat: number; lng: number }
) =>
  Math.abs(left.lat - right.lat) < 1e-7 &&
  Math.abs(left.lng - right.lng) < 1e-7;

export const getSegmentPathArrows = (
  path: Array<{ lat: number; lng: number }>,
  segmentId: string,
  minDistanceMeters = 0
): MixedRidePathArrow[] => {
  const arrows: MixedRidePathArrow[] = [];
  let distanceSinceArrow = 0;
  for (let i = 0; i < path.length - 1; i += 1) {
    const from = path[i];
    const to = path[i + 1];
    if (isSameCoordinate(from, to)) continue;
    const length = metersBetween(from, to);
    distanceSinceArrow += length;
    if (minDistanceMeters > 0 && distanceSinceArrow < minDistanceMeters) {
      continue;
    }
    arrows.push({
      uniqueId: `${segmentId}-arrow-${i}`,
      latitude: (from.lat + to.lat) / 2,
      longitude: (from.lng + to.lng) / 2,
      heading: getMapBearing(from, to)
    });
    distanceSinceArrow = 0;
  }
  return arrows;
};

export type MixedRideBoundaryPoint = {
  latitude: number;
  longitude: number;
  date: string;
  heading: number;
};

export const getLocationAtDate = (
  locations: Array<types.IObjectRideLocation> = [],
  date?: string | null
): MixedRideBoundaryPoint | null => {
  if (!date) return null;
  const target = parseLocationTime(date);
  if (Number.isNaN(target)) return null;

  const points = locations
    .filter(
      (location) =>
        location.latitude != null &&
        location.longitude != null &&
        !Number.isNaN(location.latitude) &&
        !Number.isNaN(location.longitude) &&
        !Number.isNaN(parseLocationTime(location.date))
    )
    .sort(
      (left, right) =>
        parseLocationTime(left.date) - parseLocationTime(right.date)
    );
  if (!points.length) return null;

  let before: types.IObjectRideLocation | null = null;
  let after: types.IObjectRideLocation | null = null;
  for (const location of points) {
    const time = parseLocationTime(location.date);
    if (time <= target) before = location;
    if (time >= target) {
      after = location;
      break;
    }
  }

  const toPoint = (
    location: types.IObjectRideLocation,
    heading: number
  ): MixedRideBoundaryPoint => ({
    latitude: location.latitude as number,
    longitude: location.longitude as number,
    date: location.date,
    heading
  });

  if (before && parseLocationTime(before.date) === target) {
    return toPoint(
      before,
      after && after !== before
        ? getMapBearing(
            { lat: before.latitude as number, lng: before.longitude as number },
            { lat: after.latitude as number, lng: after.longitude as number }
          )
        : getTransitionHeading(locations, before)
    );
  }
  if (after && parseLocationTime(after.date) === target) {
    return toPoint(
      after,
      before
        ? getMapBearing(
            { lat: before.latitude as number, lng: before.longitude as number },
            { lat: after.latitude as number, lng: after.longitude as number }
          )
        : getTransitionHeading(locations, after)
    );
  }
  if (before && after) {
    const start = parseLocationTime(before.date);
    const end = parseLocationTime(after.date);
    if (end - start > POLYLINE_STITCH_MS) return null;
    const ratio = end === start ? 0 : (target - start) / (end - start);
    return {
      latitude:
        (before.latitude as number) +
        ((after.latitude as number) - (before.latitude as number)) * ratio,
      longitude:
        (before.longitude as number) +
        ((after.longitude as number) - (before.longitude as number)) * ratio,
      date,
      heading: getMapBearing(
        { lat: before.latitude as number, lng: before.longitude as number },
        { lat: after.latitude as number, lng: after.longitude as number }
      )
    };
  }

  const fallback = before || after;
  if (!fallback) return null;
  if (
    Math.abs(parseLocationTime(fallback.date) - target) > POLYLINE_STITCH_MS
  ) {
    return null;
  }
  return toPoint(fallback, getTransitionHeading(locations, fallback));
};

type TimedMapPoint = { lat: number; lng: number; time: number };

const toTimedMapPoints = (
  locations: Array<types.IObjectRideLocation> = []
): TimedMapPoint[] =>
  locations
    .flatMap((location) => {
      const time = parseLocationTime(location.date);
      if (
        Number.isNaN(time) ||
        location.latitude == null ||
        location.longitude == null ||
        Number.isNaN(location.latitude) ||
        Number.isNaN(location.longitude)
      ) {
        return [];
      }
      return [
        {
          lat: location.latitude,
          lng: location.longitude,
          time
        }
      ];
    })
    .sort((left, right) => left.time - right.time);

const segmentOwnsTrack = (
  segment: IObjectRideSegment,
  points: TimedMapPoint[]
) => {
  const start = parseLocationTime(segment.startDate);
  const end = segment.endDate ? parseLocationTime(segment.endDate) : Infinity;
  if (Number.isNaN(start)) return false;
  return points.some((point) => point.time > start && point.time < end);
};

const segmentHidesBoundaryLocation = (
  segment: IObjectRideSegment | null | undefined,
  points: TimedMapPoint[]
) => {
  if (!segment) return false;
  if (segment.isPrivacyHidden) return true;
  return (
    normalizeSegmentMode(segment.mode) === ObjectRideModeEnum.PRIVATE &&
    !segmentOwnsTrack(segment, points)
  );
};

// Shared boundary samples are not a route. Connecting them draws a straight
// line across a private stretch the API did not send GPS for.
const gapContainsEmptySegment = (
  fromTime: number,
  toTime: number,
  segments: IObjectRideSegment[],
  points: TimedMapPoint[]
) => {
  if (!(toTime > fromTime)) return false;
  return segments.some((segment) => {
    const start = parseLocationTime(segment.startDate);
    const end = segment.endDate ? parseLocationTime(segment.endDate) : Infinity;
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return false;
    const contained = start >= fromTime && end <= toTime;
    return contained && !segmentOwnsTrack(segment, points);
  });
};

export const getSegmentPolylinePath = (
  locations: Array<types.IObjectRideLocation> = [],
  segment: IObjectRideSegment,
  segments: IObjectRideSegment[] = []
): Array<{ lat: number; lng: number }> => {
  const start = parseLocationTime(segment.startDate);
  const end = segment.endDate ? parseLocationTime(segment.endDate) : Infinity;
  if (Number.isNaN(start)) return [];

  const timed = toTimedMapPoints(locations);
  const inRange = timed.filter(
    (point) => point.time >= start && point.time <= end
  );
  const isPrivate =
    normalizeSegmentMode(segment.mode) === ObjectRideModeEnum.PRIVATE;
  const hasInterior = segmentOwnsTrack(segment, inRange);

  if (isPrivate && !hasInterior) return [];
  if (!inRange.length) return [];

  const points = [...inRange];
  const previous = [...timed].reverse().find((point) => point.time < start);
  if (
    previous &&
    start - previous.time <= POLYLINE_STITCH_MS &&
    !gapContainsEmptySegment(previous.time, points[0].time, segments, timed)
  ) {
    const startPoint = getLocationAtDate(locations, segment.startDate);
    const startCoord = startPoint
      ? { lat: startPoint.latitude, lng: startPoint.longitude }
      : null;
    if (startCoord && !isSameCoordinate(startCoord, points[0])) {
      points.unshift({ ...startCoord, time: start });
    }
  }

  const next = timed.find((point) => point.time > end);
  const last = points[points.length - 1];
  if (
    next &&
    Number.isFinite(end) &&
    next.time - end <= POLYLINE_STITCH_MS &&
    !gapContainsEmptySegment(last.time, next.time, segments, timed)
  ) {
    const endPoint = getLocationAtDate(locations, segment.endDate);
    const endCoord = endPoint
      ? { lat: endPoint.latitude, lng: endPoint.longitude }
      : null;
    if (endCoord && !isSameCoordinate(endCoord, last)) {
      points.push({ ...endCoord, time: end });
    }
  }

  if (points.length < 2) return [];
  return points.map(({ lat, lng }) => ({ lat, lng }));
};

const getTransitionHeading = (
  locations: Array<types.IObjectRideLocation>,
  location: types.IObjectRideLocation
): number => {
  const index = locations.findIndex(
    (item) => item.uniqueId === location.uniqueId
  );
  const previous = index > 0 ? locations[index - 1] : null;
  const next = index >= 0 ? locations[index + 1] : null;
  const current = {
    lat: location.latitude ?? 0,
    lng: location.longitude ?? 0
  };
  if (
    previous?.latitude != null &&
    previous.longitude != null &&
    next?.latitude != null &&
    next.longitude != null
  ) {
    return getMapBearing(
      { lat: previous.latitude, lng: previous.longitude },
      { lat: next.latitude, lng: next.longitude }
    );
  }
  if (previous?.latitude != null && previous.longitude != null) {
    return getMapBearing(
      { lat: previous.latitude, lng: previous.longitude },
      current
    );
  }
  if (next?.latitude != null && next.longitude != null) {
    return getMapBearing(current, { lat: next.latitude, lng: next.longitude });
  }
  return 90;
};

const findLocationNearestToDate = (
  locations: Array<types.IObjectRideLocation>,
  date: string
) => {
  const target = parseLocationTime(date);
  if (Number.isNaN(target) || !locations.length) return null;

  return locations.reduce(
    (closest, location) => {
      const locationTime = parseLocationTime(location.date);
      if (Number.isNaN(locationTime)) return closest;
      if (!closest) return location;
      const closestTime = parseLocationTime(closest.date);
      return Math.abs(locationTime - target) < Math.abs(closestTime - target)
        ? location
        : closest;
    },
    null as types.IObjectRideLocation | null
  );
};

const findSegmentExitLocation = (
  locations: Array<types.IObjectRideLocation>,
  segment: IObjectRideSegment,
  nextStartDate?: string | null
) => {
  const segmentEndDate = segment.endDate || nextStartDate || segment.startDate;
  const start = parseLocationTime(segment.startDate);
  const exclusiveEnd = nextStartDate
    ? parseLocationTime(nextStartDate)
    : parseLocationTime(segmentEndDate);
  const segmentEnd = parseLocationTime(segmentEndDate);
  if (Number.isNaN(start) || Number.isNaN(exclusiveEnd)) {
    return findLocationNearestToDate(locations, segmentEndDate);
  }

  const lastBeforeNext = [...locations]
    .filter((location) => {
      const time = parseLocationTime(location.date);
      return !Number.isNaN(time) && time >= start && time < exclusiveEnd;
    })
    .at(-1);

  if (lastBeforeNext) {
    const lastTime = parseLocationTime(lastBeforeNext.date);
    if (
      Number.isNaN(lastTime) ||
      Number.isNaN(segmentEnd) ||
      segmentEnd - lastTime <= POLYLINE_STITCH_MS
    ) {
      return lastBeforeNext;
    }
  }

  const nearestToEnd = findLocationNearestToDate(locations, segmentEndDate);
  const nearestTime = parseLocationTime(nearestToEnd?.date);
  if (
    nearestToEnd &&
    !Number.isNaN(nearestTime) &&
    !Number.isNaN(segmentEnd) &&
    Math.abs(nearestTime - segmentEnd) <= POLYLINE_STITCH_MS
  ) {
    return nearestToEnd;
  }

  return lastBeforeNext || nearestToEnd;
};

export const sliceLocationsForSegment = (
  locations: Array<types.IObjectRideLocation> = [],
  segment: IObjectRideSegment
): Array<types.IObjectRideLocation> => {
  const start = parseLocationTime(segment.startDate);
  const end = segment.endDate ? parseLocationTime(segment.endDate) : Infinity;
  if (Number.isNaN(start)) return [];

  const inRange = locations.filter((location) => {
    const time = parseLocationTime(location.date);
    if (Number.isNaN(time)) return false;
    return time >= start && time <= end;
  });

  if (!inRange.length) {
    const nearest = findLocationNearestToDate(locations, segment.startDate);
    if (!nearest) return [];
    const nearestTime = parseLocationTime(nearest.date);
    if (
      Number.isNaN(nearestTime) ||
      Math.abs(nearestTime - start) > POLYLINE_STITCH_MS
    ) {
      return [];
    }
    return [nearest];
  }

  const firstTime = parseLocationTime(inRange[0].date);
  if (firstTime > start) {
    const before = [...locations]
      .reverse()
      .find((location) => parseLocationTime(location.date) < start);
    if (before) {
      const beforeTime = parseLocationTime(before.date);
      if (
        !Number.isNaN(beforeTime) &&
        start - beforeTime <= POLYLINE_STITCH_MS
      ) {
        return [before, ...inRange];
      }
    }
  }

  return inRange;
};

export const getSegmentTransitionPoints = (
  segments: IObjectRideSegment[] = [],
  locations: Array<types.IObjectRideLocation> = []
): MixedRideTransitionPoint[] => {
  if (segments.length < 2) return [];

  return segments.slice(1).flatMap((segment, index) => {
    const previous = segments[index];
    if (previous.isPrivacyHidden || segment.isPrivacyHidden) {
      return [];
    }
    if (
      normalizeSegmentMode(previous.mode) === normalizeSegmentMode(segment.mode)
    ) {
      return [];
    }
    const previousEnd = parseLocationTime(previous.endDate);
    const nextStart = parseLocationTime(segment.startDate);
    if (
      !Number.isNaN(previousEnd) &&
      !Number.isNaN(nextStart) &&
      nextStart - previousEnd > POLYLINE_STITCH_MS
    ) {
      return [];
    }
    const boundaryDate = previous.endDate || segment.startDate;
    const location = getLocationAtDate(locations, boundaryDate);

    if (!location) {
      return [];
    }

    const timed = toTimedMapPoints(locations);
    const boundaryTime = parseLocationTime(boundaryDate);
    const sample = timed.find(
      (point) =>
        point.time === parseLocationTime(location.date) &&
        point.lat === location.latitude &&
        point.lng === location.longitude
    );
    if (!sample && !Number.isNaN(boundaryTime)) {
      const before = [...timed]
        .reverse()
        .find((point) => point.time < boundaryTime);
      const after = timed.find((point) => point.time > boundaryTime);
      if (
        before &&
        after &&
        gapContainsEmptySegment(before.time, after.time, segments, timed)
      ) {
        return [];
      }
    }

    return [
      {
        uniqueId: `${previous.uniqueId}-${segment.uniqueId}`,
        latitude: location.latitude,
        longitude: location.longitude,
        date: location.date,
        fromMode: previous.mode,
        toMode: segment.mode,
        heading: location.heading
      }
    ];
  });
};

const locationAddress = (location?: types.IObjectRideLocation | null) =>
  location?.address && location.address !== "N/A" ? location.address : "";

export const getSegmentEndpoints = (
  ride: IObjectRideWithSegments | types.IObjectRide | null | undefined,
  segment: IObjectRideSegment,
  locations: Array<types.IObjectRideLocation> = []
): {
  start: MixedRideSegmentEndpoint;
  end: MixedRideSegmentEndpoint;
} | null => {
  if (!ride) return null;

  const segments = isMixedRideWithSegments(ride)
    ? getMixedRideDisplaySegments(ride)
    : (ride as IObjectRideWithSegments).segments || [];
  const isFirstSegment = segments[0]?.uniqueId === segment.uniqueId;
  const isLastSegment =
    segments[segments.length - 1]?.uniqueId === segment.uniqueId;
  const sliced = sliceLocationsForSegment(locations, segment);
  const segmentIndex = segments.findIndex(
    (item) => item.uniqueId === segment.uniqueId
  );
  const previousSegment = segments[segmentIndex - 1];
  const nextSegment = segments[segmentIndex + 1];

  const startLocation = isFirstSegment
    ? undefined
    : previousSegment?.isPrivacyHidden
      ? sliced[0]
      : (previousSegment
          ? findSegmentExitLocation(
              locations,
              previousSegment,
              segment.startDate
            )
          : null) ||
        findLocationNearestToDate(locations, segment.startDate) ||
        sliced[0];
  const endLocation = isLastSegment
    ? undefined
    : findSegmentExitLocation(
        locations,
        segment,
        nextSegment?.isPrivacyHidden ? undefined : nextSegment?.startDate
      ) || sliced[sliced.length - 1];

  const startLatitude = isFirstSegment
    ? (ride.startLatitude ?? startLocation?.latitude ?? sliced[0]?.latitude)
    : startLocation?.latitude;
  const startLongitude = isFirstSegment
    ? (ride.startLongitude ?? startLocation?.longitude ?? sliced[0]?.longitude)
    : startLocation?.longitude;
  const startAddress = isFirstSegment
    ? ride.startAddress ||
      ride.correctedStartAddress ||
      locationAddress(startLocation)
    : locationAddress(startLocation);

  const endLatitude = isLastSegment
    ? (ride.endLatitude ??
      endLocation?.latitude ??
      sliced[sliced.length - 1]?.latitude)
    : endLocation?.latitude;
  const endLongitude = isLastSegment
    ? (ride.endLongitude ??
      endLocation?.longitude ??
      sliced[sliced.length - 1]?.longitude)
    : endLocation?.longitude;
  const endAddress = isLastSegment
    ? ride.endAddress ||
      ride.correctedEndAddress ||
      locationAddress(endLocation)
    : locationAddress(endLocation);

  if (
    startLatitude == null ||
    startLongitude == null ||
    endLatitude == null ||
    endLongitude == null
  ) {
    return null;
  }

  return {
    start: {
      date: startLocation?.date || segment.startDate,
      address: startAddress,
      latitude: startLatitude,
      longitude: startLongitude
    },
    end: {
      date: endLocation?.date || segment.endDate || ride.endDate,
      address: endAddress,
      latitude: endLatitude,
      longitude: endLongitude
    }
  };
};

export const buildMixedRideTimelineStops = (
  ride: IObjectRideWithSegments | types.IObjectRide | null | undefined,
  segments: IObjectRideSegment[] = [],
  locations: Array<types.IObjectRideLocation> = []
): MixedRideTimelineStop[] => {
  if (!ride || !segments.length) return [];

  const points = toTimedMapPoints(locations);
  const startHasCoordinates =
    ride.startLatitude != null && ride.startLongitude != null;
  const startLocation = startHasCoordinates
    ? findLocationNearestToDate(
        locations,
        segments[0].startDate || ride.startDate
      )
    : null;
  const hideStart =
    !startHasCoordinates || segmentHidesBoundaryLocation(segments[0], points);
  const stops: MixedRideTimelineStop[] = [
    {
      date: segments[0].startDate || ride.startDate,
      address: hideStart
        ? ""
        : ride.startAddress ||
          ride.correctedStartAddress ||
          startLocation?.address ||
          "",
      latitude: hideStart
        ? null
        : (ride.startLatitude ?? startLocation?.latitude),
      longitude: hideStart
        ? null
        : (ride.startLongitude ?? startLocation?.longitude),
      followingSegment: segments[0]
    }
  ];

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const date =
      segment.endDate ||
      (isLast ? ride.endDate : segments[index + 1]?.startDate) ||
      segment.startDate;

    if (isLast) {
      const endHasCoordinates =
        ride.endLatitude != null && ride.endLongitude != null;
      const hideEnd =
        !endHasCoordinates || segmentHidesBoundaryLocation(segment, points);
      const nearest = hideEnd
        ? null
        : findLocationNearestToDate(locations, date);
      stops.push({
        date: hideEnd && ride.endDate ? ride.endDate : date,
        address: hideEnd
          ? ""
          : ride.endAddress ||
            ride.correctedEndAddress ||
            nearest?.address ||
            "",
        latitude: hideEnd ? null : (ride.endLatitude ?? nearest?.latitude),
        longitude: hideEnd ? null : (ride.endLongitude ?? nearest?.longitude),
        followingSegment: null
      });
      return;
    }

    const next = segments[index + 1];
    const hideBoundary =
      segmentHidesBoundaryLocation(segment, points) ||
      segmentHidesBoundaryLocation(next, points);

    if (hideBoundary) {
      stops.push({
        date: next?.startDate || date,
        address: "",
        latitude: null,
        longitude: null,
        followingSegment: next
      });
      return;
    }

    const nearest = findLocationNearestToDate(locations, date);
    const stopLocation =
      findSegmentExitLocation(locations, segment, next?.startDate) || nearest;

    stops.push({
      date: stopLocation?.date || date,
      address: locationAddress(stopLocation),
      latitude: stopLocation?.latitude,
      longitude: stopLocation?.longitude,
      followingSegment: next
    });
  });

  return stops;
};
